import {Client} from '@stomp/stompjs';
import {SiteSettings} from './base';
import {defer, Deferred} from "./core/async";
import * as Cookies from 'js-cookie';
import {Moment} from "moment";
import Minilog from 'minilog';

let logger = Minilog('frontend.models.reports');

/**
 * Class for invoking RPC asynchronously using an AMQP broker connection.
 */
export class BrokerRpcInvoker {
    replyRoutingKey: string;
    sent: boolean;
    deferred: Deferred;
    client: Client;
    brokerData: any;

    constructor(brokerData) {
        this.brokerData = brokerData;
        this.deferred = defer();
        this.sent = false;
        this.replyRoutingKey = '/temp-queue/rpc-reply-queue';
    }

    /**
     * Parses received message from broker. Resolves/rejects the RPC call
     * if it's a valid response and shuts down the connection.
     *
     * @param msg
     * @param correlationId
     */
    onReceive(msg, correlationId) {
        logger.debug('onReceived: ', msg, ' correlation ID: ', correlationId);
        if (!this.deferred
            || msg.headers['correlation-id'] !== correlationId) {
            return;
        }
        let data = JSON.parse(msg.body);
        if (data.url) {
            this.deferred.resolve(data);
        } else {
            this.deferred.reject(new Error(data.error));
        }
        // once message received, shut down connection
        this.stop();
    }

    /**
     * Handles connection events. Sends RPC request if required.
     *
     * @param status
     * @param payload
     */
    onConnect(status, payload) {
        logger.debug('onConnect ', status);
        if (!this.sent) {
            this.client.publish(payload);
            this.sent = true;
        }
    }

    /**
     * Handles any errors. Rejects RPC call if triggered.
     *
     * @param error
     */
    onError(error) {
        logger.warn('onError ', error);
        this.deferred.reject(
            new Error(
                "Failed to connect to report service. Please try again later."
            )
        );
    }

    /**
     * Starts the configured report generation process, sending the given
     * message to the report service and returning a promise. When the report
     * is processed, promise is resolved or rejected as appropriate.
     */
    start(message) {
        let correlationId = Math.random().toString();
        let payload = {
            destination: '/topic/' + SiteSettings.rpc.storeExportTopic,
            body: JSON.stringify(message),
            headers: {
                'correlation-id': correlationId,
                'reply-to': this.replyRoutingKey
            }
        };
        this.client = new Client({
            brokerURL: SiteSettings.uri.stomp,
            connectHeaders: {
                login: this.brokerData.username,
                passcode: this.brokerData.password,
                host: this.brokerData.vhost
            },
            debug: (str) => {
                logger.debug('DEBUG ', str);
            },
            onConnect: (status) => {
                this.onConnect(status, payload);
            },
            onUnhandledMessage: (msg) => {
                this.onReceive(msg, correlationId);
            },
            onWebSocketClose: (event) => {
                // force re-send of original request on re-connect
                // because RabbitMQ discards the message if the client
                // disconnects
                this.sent = false;
            },
            onStompError: this.onError.bind(this),
            reconnectDelay: 100
        });
        this.client.activate();
        return this.deferred.promise;
    }

    /**
     * Stops report generation if in progress. Disconnects from report
     * service.
     */
    stop() {
        if (this.deferred) {
            this.deferred.promise.cancel();
        }
        this.deferred = null;
        if (this.client) {
            this.client.deactivate();
        }
        this.client = null;
    }
}

let _rpcInstance: BrokerRpcInvoker = null;

/**
 * Thunk to start export of store report via RPC call. Returns promise with
 * eventual response.
 *
 * @param premisesId
 * @param premisesName
 * @param startDate
 * @param endDate
 * @param langcode
 * @param reportType
 * @param sensors
 * @param intervalSeconds
 */
export function loadStoreExport(premisesId: number,
                                premisesName: String,
                                startDate: Moment,
                                endDate: Moment,
                                langcode: string,
                                reportType: string,
                                sensors?: Array<number>,
                                intervalSeconds?: number) {
    return function (dispatch, getState) {
        let userData = getState().data.user;
        let interval = null;
        if (intervalSeconds) {
            interval = {seconds: intervalSeconds};
        }
        let message = {
            from_time: startDate.startOf('day').format('YYYY-MM-DD HH:mm'),
            to_time: endDate.endOf('day').format('YYYY-MM-DD HH:mm'),
            timezone: userData.timezone,
            user: userData.username,
            // inject authentication data for the user
            sessionid: Cookies.get('sessionid'),
            premises_name: premisesName,
            report_type: reportType,
            temperature_uom: userData.temperature_uom,
            langcode,
            interval,
            unit_sensors: undefined,
            premises: undefined
        };
        ExportReportTypes.areSensorsRequired(reportType) ?
            message.unit_sensors = sensors :
            message.premises = [premisesId];

        // ensure only one report can be generated at a time
        // and clean up connection data
        if (_rpcInstance) {
            _rpcInstance.stop();
        }
        _rpcInstance = new BrokerRpcInvoker(userData.broker);
        return _rpcInstance.start(message);
    };
}

export function loadCabinetExport(cabinet, startDate, endDate, langcode, reportType, sensors) {
    // FIXME: should be exporting cabinet data, not cabinet's premises data
    return loadStoreExport(cabinet.gateway.premises.id, cabinet.gateway.premises.name, startDate, endDate, langcode, reportType, sensors);
}

/**
 * Enumeration of report types
 */
export class ExportReportTypes {
    // report resources
    static HACCP = 'HACCP';
    static DATA = 'DATA';
    static DATA_DUMP = 'DATA_DUMP';
    static HACCP_INTERVAL = 'HACCP_INTERVAL';

    static areSensorsRequired(reportType: ExportReportTypes) {
        return reportType === this.HACCP;
    }

    static isDump(reportType: ExportReportTypes) {
        return reportType === this.DATA_DUMP;
    }
}
